import React from 'react';
import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  Image,
  pdf,
} from '@react-pdf/renderer';
import { VoucherViewData } from 'src/models/Management.interface';
import { getCompanyInfoForPDF, CompanyInfoData } from 'src/utils/company.utils';
import { formatDate, formatCurrency } from 'src/utils/pdf.utils';
import Logo from 'src/assets/images/Logo.png';
import { VoucherStatus } from 'src/models/enums/VoucherStatus.enum';

const colors = {
  primary: '#1a3c5e',
  primaryLight: '#3a7bd5',
  textDark: '#1a1a2e',
  textMedium: '#555555',
  textLight: '#888888',
  border: '#e0e0e0',
  borderLight: '#f0f0f0',
  backgroundGray: '#f7f8fa',
  white: '#ffffff',
  orange: '#e67e22',
  blue: '#3a7bd5',
  success: '#27ae60',
  red: '#c0392b',
};

const styles = StyleSheet.create({
  page: {
    paddingTop: 40,
    paddingLeft: 40,
    paddingRight: 40,
    paddingBottom: 100,
    fontSize: 10,
    fontFamily: 'Helvetica',
    backgroundColor: colors.white,
  },

  // Title
  voucherTitle: {
    fontSize: 24,
    fontWeight: 'bold',
    color: colors.primary,
    textAlign: 'center',
    marginBottom: 12,
  },
  voucherTitleDivider: {
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
    marginBottom: 24,
  },

  // Header row
  headerRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 24,
    paddingBottom: 16,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
  },
  headerLeft: {
    flexDirection: 'column',
    alignItems: 'flex-start',
    width: '50%',
  },
  companyLogo: {
    width: 60,
    height: 60,
    marginBottom: 8,
  },
  companyTextBlock: {
    flexDirection: 'column',
  },
  companyName: {
    fontSize: 14,
    fontWeight: 'bold',
    color: colors.primary,
    marginBottom: 2,
  },
  companyDetails: {
    fontSize: 9,
    color: colors.textMedium,
    fontWeight: 'normal',
    marginBottom: 2,
  },
  headerRight: {
    width: '45%',
  },
  voucherDetailRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 6,
    paddingBottom: 5,
    borderBottomWidth: 1,
    borderBottomColor: colors.borderLight,
  },
  voucherDetailLabel: {
    fontSize: 9,
    color: colors.textLight,
  },
  voucherDetailValue: {
    fontSize: 9,
    fontWeight: 'bold',
    color: colors.textDark,
  },
  voucherAmountValue: {
    fontSize: 18,
    fontWeight: 'bold',
    color: colors.textDark,
  },

  // Content Section
  contentSection: {
    marginTop: 10,
    marginBottom: 20,
  },
  sectionTitle: {
    fontSize: 12,
    fontWeight: 'bold',
    color: colors.primary,
    marginBottom: 12,
    paddingBottom: 4,
    borderBottomWidth: 1,
    borderBottomColor: colors.primary,
    width: '100%',
  },
  detailGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
  },
  detailItem: {
    width: '48%',
    marginBottom: 12,
    padding: 8,
    backgroundColor: colors.backgroundGray,
    borderRadius: 4,
  },
  detailLabel: {
    fontSize: 8,
    color: colors.textLight,
    marginBottom: 4,
    textTransform: 'uppercase',
  },
  detailValue: {
    fontSize: 10,
    color: colors.textDark,
    fontWeight: 'normal',
  },
  descriptionBox: {
    width: '100%',
    marginTop: 8,
    padding: 10,
    backgroundColor: colors.backgroundGray,
    borderRadius: 4,
    minHeight: 60,
  },

  // Amount Summary table-like
  summaryContainer: {
    marginTop: 20,
    padding: 12,
    backgroundColor: colors.backgroundGray,
    borderRadius: 4,
    alignSelf: 'flex-end',
    width: '40%',
  },
  summaryRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingVertical: 4,
  },
  summaryRowFinal: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingVertical: 6,
    marginTop: 4,
    borderTopWidth: 1,
    borderTopColor: colors.border,
  },
  summaryText: {
    fontSize: 9,
    color: colors.textMedium,
  },
  summaryValue: {
    fontSize: 10,
    fontWeight: 'bold',
    color: colors.textDark,
  },
  finalAmountText: {
    fontSize: 11,
    fontWeight: 'bold',
    color: colors.primary,
  },

  // Footer
  footer: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#F1F7FF',
    paddingVertical: 12,
    paddingHorizontal: 40,
  },
  footerText: {
    fontSize: 10,
    color: colors.primary,
    fontWeight: 'bold',
    marginRight: 8,
    letterSpacing: 1,
  },
  footerLogo: {
    width: 60,
    height: 20,
    marginBottom: 2,
  },
});

interface VoucherPDFProps {
  voucher: VoucherViewData;
  companyInfo?: CompanyInfoData;
}

export const VoucherDocument: React.FC<VoucherPDFProps> = ({
  voucher,
  companyInfo,
}) => {
  const balanceAmount = voucher.billAmount - voucher.totalPaidAmount;

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* Footer */}
        <View style={styles.footer} fixed>
          <Text style={styles.footerText}>POWERED BY</Text>
          <Image style={styles.footerLogo} src={Logo} />
        </View>

        {/* ===== VOUCHER TITLE ===== */}
        <Text style={styles.voucherTitle}>PAYMENT VOUCHER</Text>
        <View style={styles.voucherTitleDivider} />

        {/* ===== HEADER ROW ===== */}
        <View style={styles.headerRow}>
          {/* Company Info - Left Side */}
          <View style={styles.headerLeft}>
            {companyInfo?.logoUrl && (
              <Image style={styles.companyLogo} src={companyInfo.logoUrl} />
            )}
            <View style={styles.companyTextBlock}>
              <Text style={styles.companyName}>
                {companyInfo?.companyName || 'OrderX'}
              </Text>
              {companyInfo && (
                <>
                  <Text style={styles.companyDetails}>
                    {companyInfo.addressLine1}
                    {companyInfo.addressLine2
                      ? `, ${companyInfo.addressLine2}`
                      : ''}
                  </Text>
                  <Text style={styles.companyDetails}>
                    {companyInfo.city}, {companyInfo.postCode}
                  </Text>
                  <Text style={styles.companyDetails}>
                    {companyInfo.country}
                  </Text>
                  {companyInfo.telephoneNumber && (
                    <Text style={styles.companyDetails}>
                      Tel: {companyInfo.telephoneNumber}
                    </Text>
                  )}
                  {companyInfo.email && (
                    <Text style={styles.companyDetails}>
                      Email: {companyInfo.email}
                    </Text>
                  )}
                </>
              )}
            </View>
          </View>

          {/* Voucher Details - Right Side */}
          <View style={styles.headerRight}>
            <View style={styles.voucherDetailRow}>
              <Text style={styles.voucherDetailLabel}>Bill Number</Text>
              <Text style={styles.voucherDetailValue}>
                {voucher.billNumber || '-'}
              </Text>
            </View>
            <View style={styles.voucherDetailRow}>
              <Text style={styles.voucherDetailLabel}>Date</Text>
              <Text style={styles.voucherDetailValue}>
                {formatDate(voucher.createdAt)}
              </Text>
            </View>
            <View style={styles.voucherDetailRow}>
              <Text style={styles.voucherDetailLabel}>Status</Text>
              <Text style={styles.voucherDetailValue}>{voucher.status}</Text>
            </View>
            <View style={styles.voucherDetailRow}>
              <Text style={styles.voucherDetailLabel}>Amount</Text>
              <Text style={styles.voucherAmountValue}>
                {formatCurrency(voucher.billAmount)}
              </Text>
            </View>
          </View>
        </View>

        {/* ===== VOUCHER DETAILS SECTION ===== */}
        <View style={styles.contentSection}>
          <Text style={styles.sectionTitle}>VOUCHER INFORMATION</Text>
          <View style={styles.detailGrid}>
            <View style={styles.detailItem}>
              <Text style={styles.detailLabel}>Payee / Recipient</Text>
              <Text style={styles.detailValue}>{voucher.payee}</Text>
            </View>
            <View style={styles.detailItem}>
              <Text style={styles.detailLabel}>Voucher Type</Text>
              <Text style={styles.detailValue}>{voucher.voucherType}</Text>
            </View>
            <View style={styles.detailItem}>
              <Text style={styles.detailLabel}>Prepared By</Text>
              <Text style={styles.detailValue}>
                {voucher.preparedBy || '-'}
              </Text>
            </View>
            <View style={styles.detailItem}>
              <Text style={styles.detailLabel}>Approved By</Text>
              <Text style={styles.detailValue}>
                {voucher.approvedBy || '-'}
              </Text>
            </View>
          </View>
        </View>

        {/* ===== DESCRIPTION SECTION ===== */}
        {voucher.description && (
          <View style={styles.contentSection}>
            <Text style={styles.sectionTitle}>DESCRIPTION</Text>
            <View style={styles.descriptionBox}>
              <Text style={styles.detailValue}>{voucher.description}</Text>
            </View>
          </View>
        )}

        {/* ===== SUMMARY SECTION ===== */}
        <View style={styles.summaryContainer}>
          <View style={styles.summaryRow}>
            <Text style={styles.summaryText}>Voucher Amount</Text>
            <Text style={styles.summaryValue}>
              {formatCurrency(voucher.billAmount)}
            </Text>
          </View>
          <View style={styles.summaryRow}>
            <Text style={styles.summaryText}>Paid Amount</Text>
            <Text style={styles.summaryValue}>
              {formatCurrency(voucher.totalPaidAmount)}
            </Text>
          </View>
          <View style={styles.summaryRowFinal}>
            <Text style={styles.finalAmountText}>Balance Due</Text>
            <Text style={styles.finalAmountText}>
              {formatCurrency(balanceAmount)}
            </Text>
          </View>
        </View>
      </Page>
    </Document>
  );
};

export const generateVoucherPDF = async (
  voucher: VoucherViewData
): Promise<void> => {
  try {
    const companyInfo = await getCompanyInfoForPDF();

    const blob = await pdf(
      <VoucherDocument voucher={voucher} companyInfo={companyInfo} />
    ).toBlob();

    const fileName = `Voucher-${
      voucher.billNumber || voucher.id.substring(0, 8)
    }.pdf`;
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    link.target = '_blank';
    link.click();
    setTimeout(() => URL.revokeObjectURL(url), 100);
  } catch (error) {
    console.error('Error generating Voucher PDF:', error);
    throw error;
  }
};
